import { useQuery } from '@tanstack/react-query';
import {
  getInactiveSensors,
  getInactiveSensorsSummary,
  getFleetSummary,
  getOrganisationsWithInactiveSensors,
  getSensorDetail,
} from '../api';
import type { InactiveSensorsFilters } from '../types';

// =============================================================================
// Query Keys Factory
// =============================================================================

export const inactiveSensorsKeys = {
  all: ['inactive-sensors'] as const,
  list: (filters?: InactiveSensorsFilters) =>
    [...inactiveSensorsKeys.all, 'list', filters] as const,
  summary: () => [...inactiveSensorsKeys.all, 'summary'] as const,
  organisations: () => [...inactiveSensorsKeys.all, 'organisations'] as const,
  fleetSummary: () => [...inactiveSensorsKeys.all, 'fleet-summary'] as const,
  detail: (id: string) => [...inactiveSensorsKeys.all, 'detail', id] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch inactive sensors list with filtering and pagination
 */
export function useInactiveSensors(filters: InactiveSensorsFilters = {}) {
  return useQuery({
    queryKey: inactiveSensorsKeys.list(filters),
    queryFn: () => getInactiveSensors(filters),
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
  });
}

/**
 * Hook to fetch inactive sensors summary statistics
 */
export function useInactiveSensorsSummary() {
  return useQuery({
    queryKey: inactiveSensorsKeys.summary(),
    queryFn: getInactiveSensorsSummary,
    staleTime: 60 * 1000, // Summary can be stale for 1 minute
  });
}

/**
 * Hook to fetch fleet-wide sensor summary (all statuses)
 */
export function useFleetSummary() {
  return useQuery({
    queryKey: inactiveSensorsKeys.fleetSummary(),
    queryFn: getFleetSummary,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch organisations for dropdown filter
 */
export function useOrganisationsWithInactiveSensors() {
  return useQuery({
    queryKey: inactiveSensorsKeys.organisations(),
    queryFn: getOrganisationsWithInactiveSensors,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to fetch full sensor detail by mongoSensorId
 */
export function useSensorDetail(sensorId: string | null) {
  return useQuery({
    queryKey: inactiveSensorsKeys.detail(sensorId!),
    queryFn: () => getSensorDetail(sensorId!),
    enabled: !!sensorId,
    staleTime: 30 * 1000,
  });
}
